import type { RefObject } from "react"

// Only prepend once a digit follows the dot, so typing "." alone still
// shows "." and only becomes "0.5" once the "5" is typed.
export const normalizeLeadingDot = (input: string): string =>
  /^\.\d+$/.test(input) ? `0${input}` : input

// Strips redundant leading zeros ("05" -> "5", "007" -> "7") without
// touching a lone "0" or the legitimate leading zero in "0.5" (no digit
// immediately follows the "0" in either of those cases).
export const normalizeLeadingZero = (input: string): string =>
  input.replace(/^0+(\d)/, "$1")

export const normalizeNumericInputValue = (input: string): string =>
  normalizeLeadingZero(normalizeLeadingDot(input))

export const moveCaretAfterDecimal = (input: HTMLInputElement | null) => {
  if (!input) return

  const dotIndex = input.value.indexOf(".")
  const caretPosition = dotIndex === -1 ? input.value.length : dotIndex + 1

  input.setSelectionRange(caretPosition, caretPosition)
}

// True when normalizing the raw candidate collapses it back to the value
// already shown, even though the raw text itself differs from that value.
// Committing such an edit is invisible to React (same state -> no re-render)
// but leaves the un-normalized raw text stuck in the DOM, so it must be
// rejected outright via isAllowed rather than "corrected" after the fact.
export const isNoOpAfterNormalization = (
  rawValue: string,
  normalizedValue: string,
  currentValue: string,
) => rawValue !== normalizedValue && normalizedValue === currentValue

// Counts literal "." or "," characters in a raw (pre-clean) input string -
// used to catch a second decimal point before react-number-format's own
// dedup logic (which only recognizes "." as the decimal separator and
// silently keeps just the first one) can hide "," behind a stripped
// character, or hide "." behind a merged/truncated number.
export const countDecimalSeparators = (value: string): number =>
  (value.match(/[.,]/g) ?? []).length

// How many thousandSeparator commas a correctly grouped display of
// currentValue's integer part would contain (grouping runs of 3 digits from
// the right). Used as a ceiling on "expected" separators in the raw DOM
// value: thousandSeparator commas - whether freshly inserted or stale
// leftovers from an in-progress edit (e.g. a backspace that temporarily
// shrinks a group below 3 digits, so its shape no longer looks like a valid
// group) - are never literal decimal-separator keystrokes and must not be
// mistaken for one.
const countExpectedGroupingCommas = (currentValue: string): number => {
  const integerDigitCount = currentValue.split(".")[0]?.length ?? 0
  return integerDigitCount > 0 ? Math.floor((integerDigitCount - 1) / 3) : 0
}

// currentValue.includes(".") guarantees exactly one legitimate decimal point
// is already baked into the expected-separator ceiling below.
const LEGITIMATE_DECIMAL_POINT_COUNT = 1

// True once the current value already has a decimal point and the raw,
// not-yet-cleaned input contains more "." or "," characters than the single
// legitimate decimal point plus whatever thousand-separator grouping
// currentValue's integer part could ever produce - i.e. a literal separator
// beyond anything formatting alone explains.
export const hasStraySecondDecimalSeparator = (
  rawValue: string,
  currentValue: string,
): boolean => {
  const maxExplainableSeparators =
    countExpectedGroupingCommas(currentValue) + LEGITIMATE_DECIMAL_POINT_COUNT

  return (
    currentValue.includes(".") &&
    countDecimalSeparators(rawValue) > maxExplainableSeparators
  )
}

// True whenever a decimal separator ("." or ",") is typed immediately in
// front of the existing value (e.g. "12" -> ".12", "12.3" -> ".12.3",
// "0" -> ".0") - the signal to reset the whole field back to "." so the
// user can start a fresh number, regardless of what was there before.
// Requires an exact match on the remainder (not just "starts with a
// separator") so that typing forward after an existing "." (e.g. "." ->
// ".0") is never mistaken for a fresh prepend. currentValue is always the
// clean, comma-free numeric string held in state, so any thousandSeparator
// grouping commas in the remainder can be stripped unconditionally before
// comparing - none of them can ever be a legitimate part of currentValue.
export const isDecimalSeparatorPrepended = (
  rawValue: string,
  currentValue: string,
): boolean =>
  currentValue !== "" &&
  /^[.,]/.test(rawValue) &&
  rawValue.slice(1).replace(/,/g, "") === currentValue

// True when currentValue is mid-decimal-entry (ends in ".") and
// candidateValue simply extends it with more digits - i.e. the edit only
// grew the fraction, not the integer part in front of the dot. Excludes
// edits like "338." -> "1338." (a digit prepended), which still ends in "."
// but changes what comes before it, so it no longer starts with "338.".
export const isFractionOnlyContinuation = (
  candidateValue: string,
  currentValue: string,
): boolean =>
  currentValue.endsWith(".") && candidateValue.startsWith(currentValue)

interface ZeroFallbackBlurOptions {
  value: number | null | undefined
  internalValue: string
  onChange?: (value: number) => void
  setInternalValue: (value: string) => void
  inputRef: RefObject<HTMLInputElement>
}

// On blur, commit the field to a real number: an emptied field falls back to
// 0 (reported via onChange); anything else re-syncs internalValue to the
// numeric `value` prop (e.g. stripping a trailing "12." back to "12").
// When the committed value is 0, react-number-format sometimes doesn't flush
// the DOM to "0" in the same tick (e.g. coming from the lone-dot "." reset
// state), so the raw DOM value is force-corrected once more after render
// settles.
export const createZeroFallbackBlurHandler =
  ({
    value,
    internalValue,
    onChange,
    setInternalValue,
    inputRef,
  }: ZeroFallbackBlurOptions) =>
  () => {
    const isEmptied = internalValue === ""
    const nextValue = isEmptied ? 0 : value ?? 0

    if (isEmptied) {
      onChange?.(nextValue)
    }

    setInternalValue(nextValue.toString())

    if (nextValue === 0) {
      setTimeout(() => {
        if (inputRef.current && inputRef.current.value !== "0") {
          inputRef.current.value = "0"
        }
      }, 0)
    }
  }
